import { useState, useEffect } from "react";
import superagent from "superagent";
import { backendUrl } from '@helpers/consts';
import { format } from "date-fns";
import useQuery from '@helpers/JIFI/useQuery';
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from "@shadcn/components/ui/tabs";
import { Button } from "@shadcn/components/ui/button";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@shadcn/components/ui/table";
import { Filter } from 'lucide-react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title
} from 'chart.js';

// Import refactored components
import SaleStateReportFilterModal from './components/SaleStateReportFilterModal';
import ByWarehouseLocationTab from './components/ByWarehouseLocationTab';
import ByStoreTypeTab from './components/ByStoreTypeTab';
import RegionTab from './components/RegionTab';


ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title
);

const SaleStateReport = () => {
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [regions] = useQuery({ url: `/regionConfigs`, refresh: true });
  const regionOptions = regions?.map(r => ({ label: r.regionId, value: r.regionId })) || [];
  const [groupBy, setGroupBy] = useState("category"); 
  const [activeTab, setActiveTab] = useState("byWarehouseLocation");
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  
  // Month to Date specific states
  const [monthToDateData, setMonthToDateData] = useState([]);
  const [lastYearMonthToDateData, setLastYearMonthToDateData] = useState([]);
  const [monthToDateColorData, setMonthToDateColorData] = useState([]);
  const [loadingMonthToDate, setLoadingMonthToDate] = useState(false);
  
  const [productData] = useQuery({
    url: `/product`,
  });
  
  const [skuProductMap, setSkuProductMap] = useState({});
  
  const getCurrentMonthDateRange = () => {
    const now = new Date();
    const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
      startDate: startDate,
      endDate: endDate
    };
  };

  const [filters, setFilters] = useState({
    regions: [],
    startDate: getCurrentMonthDateRange().startDate,
    endDate: new Date(),
    categoryNames: [],
    productIds: []
  });
  
  useEffect(() => {
    if (productData?.data && Array.isArray(productData.data)) {
      const skuMap = {};
      
      productData.data.forEach(product => {
        if (product.variants && Array.isArray(product.variants)) {
          const productName = product.name || "Unknown Product";
          
          product.variants.forEach(variant => {
            if (variant.sku) {
              skuMap[variant.sku] = {
                product: productName,
                category:  product.newProductCategory?.name,
                variant: variant.name || variant.sku
              };
            }
          });
        }
      });
      
      setSkuProductMap(skuMap);
    }
  }, [productData]);

  useEffect(() => {
    
      fetchReportData().then(() => {
        
        if (activeTab === "monthToDate") {
          fetchMonthToDateData();
        }
      });
    
  }, [filters]);

  useEffect(() => {
    
    if (activeTab === "monthToDate" && reportData) {
      fetchMonthToDateData();
    }
  }, [activeTab]);

  const fetchReportData = async () => {
    setLoading(true);

    try {
      const startDate = filters?.startDate ? new Date(filters.startDate) : new Date();
      const endDate = filters?.endDate ? new Date(filters.endDate) : new Date();
      const regionFilters = Array.isArray(filters?.regions)
        ? filters.regions.filter(Boolean)
        : [];

      const payload = {
        regions: regionFilters,
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd')
      };

      if (filters?.categoryNames?.length) {
        payload.categoryNames = filters.categoryNames;
      }

      if (filters?.productIds?.length) {
        payload.productIds = filters.productIds;
      }

      const response = await superagent
        .post(`${backendUrl}/reports/sale-state`)
        .set('Authorization', `Bearer ${localStorage.getItem('authJWT')}`)
        .set('Accept', 'application/json')
        .send(payload);

      const processedData = { ...response.body };

      if (!processedData.byStoreType && processedData.byWarehouseLocation) {
        processedData.byStoreType = processedData.byWarehouseLocation.map(item => ({
          ...item,
          storeType: item.warehouseLocation?.includes('EWE') ? 'Retail' : 'Online'
        }));
      }

      setReportData(processedData);
    } catch (err) {
      console.error('Failed to load sale state report', err);
    } finally {
      setLoading(false);
    }
  };
  
  
  const processHierarchicalData = (dataSource = "byWarehouseLocation") => {
    if (!reportData || !reportData[dataSource]) {
      return {
        hierarchicalData: {},
        warehouses: [],
        total: 0
      };
    }

    let warehouseKey;
    if (dataSource === "byWarehouseLocation") {
      warehouseKey = "warehouseLocation";
    } else if (dataSource === "byStoreType") {
      warehouseKey = "storeType";
    }

    
    const warehouses = [...new Set(reportData[dataSource].map(item => item[warehouseKey]))].sort();

    const hierarchicalData = {};
    let grandTotal = 0;

    
    warehouses.forEach(warehouse => {
      hierarchicalData[warehouse] = {};
    });

    
    reportData[dataSource].forEach(item => {
      const warehouse = item[warehouseKey];
      const sku = item.sku;
      const quantity = item.totalQuantity || 0;
      const product = skuProductMap[sku]?.product || "Unknown Product";
      const category = skuProductMap[sku]?.category || "Uncategorized";

      grandTotal += quantity;

      
      if (!hierarchicalData[warehouse][category]) {
        hierarchicalData[warehouse][category] = {
          total: 0,
          products: {}
        };
      }

      
      if (!hierarchicalData[warehouse][category].products[product]) {
        hierarchicalData[warehouse][category].products[product] = {
          total: 0,
          skus: {}
        };
      }

      
      hierarchicalData[warehouse][category].products[product].skus[sku] = {
        quantity,
        sku,
        product,
        category
      };

      
      hierarchicalData[warehouse][category].products[product].total += quantity;
      hierarchicalData[warehouse][category].total += quantity;
    });

    return {
      hierarchicalData,
      warehouses,
      total: grandTotal
    };
  };

  const processTableData = (dataSource = "byWarehouseLocation") => {
      if (!reportData || !reportData[dataSource]) {
      return {
        tableData: [],
        warehouses: [],
        warehouseTotals: {},
        grandTotal: 0,
        warehouseLocationSummary: [],
        storeTypeSummary: [],
        total: 0
      };
    }
    
    let warehouseKey;
    if (dataSource === "byWarehouseLocation") {
      warehouseKey = "warehouseLocation";
    } else if (dataSource === "byStoreType") {
      warehouseKey = "storeType";
    }
    
    const warehouses = [...new Set(reportData[dataSource].map(item => item[warehouseKey]))];
    
          if (groupBy === "sku") {
        const skus = [...new Set(reportData[dataSource].map(item => item.sku))];
      
        const tableData = skus.map((sku) => {
          const skuData = { 
            sku,
            product: skuProductMap[sku]?.product || "Unknown",
            category: skuProductMap[sku]?.category || "Uncategorized"
          };
      
          warehouses.forEach(warehouse => {
            skuData[warehouse] = 0;
          });
      
          const skuItems = reportData[dataSource].filter(item => item.sku === sku);
          
          skuItems.forEach(item => {
            skuData[item[warehouseKey]] = item.totalQuantity;
          });
      
          skuData.total = warehouses.reduce((sum, warehouse) => sum + (skuData[warehouse] || 0), 0);
          
          return skuData;
        });
    
      const warehouseTotals = {};
      let grandTotal = 0;
      
      warehouses.forEach(warehouse => {
        warehouseTotals[warehouse] = tableData.reduce((sum, row) => sum + (row[warehouse] || 0), 0);
        grandTotal += warehouseTotals[warehouse];
      });

      
      const warehouseSummary = warehouses.map(warehouse => ({
        [warehouseKey]: warehouse,
        quantity: warehouseTotals[warehouse] || 0
      })).filter(item => item.quantity > 0).sort((a, b) => b.quantity - a.quantity);
      
        return {
          tableData,
          warehouses,
          warehouseTotals,
          grandTotal,
          warehouseLocationSummary: dataSource === "byWarehouseLocation" ? warehouseSummary : [],
          storeTypeSummary: dataSource === "byStoreType" ? warehouseSummary : [],
          total: grandTotal
        };
      } 
      else if (groupBy === "product") {
      const productMap = {};
      
      reportData[dataSource].forEach(item => {
        const sku = item?.sku;
        const product = skuProductMap?.[sku]?.product || "Unknown";
        const category = skuProductMap?.[sku]?.category || "Uncategorized";
        const warehouse = item?.[warehouseKey];
        const quantity = item?.totalQuantity;
        
        if (!productMap[product]) {
          productMap[product] = {
            product,
            category,
            skus: [sku]
          };
    
          warehouses.forEach(wh => {
            productMap[product][wh] = 0;
          });
        } else if (!productMap[product].skus.includes(sku)) {
          productMap[product].skus.push(sku);
        }
    
        productMap[product][warehouse] = (productMap[product][warehouse] || 0) + quantity;
      });
    
      const tableData = Object.values(productMap).map(item => {
        item.total = warehouses.reduce((sum, warehouse) => sum + (item[warehouse] || 0), 0);
        return item;
      });
    
      const warehouseTotals = {};
      let grandTotal = 0;
      
      warehouses.forEach(warehouse => {
        warehouseTotals[warehouse] = tableData.reduce((sum, row) => sum + (row[warehouse] || 0), 0);
        grandTotal += warehouseTotals[warehouse];
      });

      
      const warehouseSummary = warehouses.map(warehouse => ({
        [warehouseKey]: warehouse,
        quantity: warehouseTotals[warehouse] || 0
      })).filter(item => item.quantity > 0).sort((a, b) => b.quantity - a.quantity);
      
      return {
        tableData,
        warehouses,
        warehouseTotals,
        grandTotal,
        warehouseLocationSummary: dataSource === "byWarehouseLocation" ? warehouseSummary : [],
        storeTypeSummary: dataSource === "byStoreType" ? warehouseSummary : [],
        total: grandTotal
      };
    }
    else if (groupBy === "category") {
      const categoryMap = {};
      
      reportData[dataSource].forEach(item => {
        const sku = item?.sku;
        const category = skuProductMap?.[sku]?.category || "Uncategorized";
        const warehouse = item?.[warehouseKey];
        const quantity = item?.totalQuantity;
        
        if (!categoryMap[category]) {
          categoryMap[category] = {
            category,
            skus: [sku]
          };
    
          warehouses.forEach(wh => {
            categoryMap[category][wh] = 0;
          });
        } else if (!categoryMap[category].skus.includes(sku)) {
          categoryMap[category].skus.push(sku);
        }
    
        categoryMap[category][warehouse] = (categoryMap[category][warehouse] || 0) + quantity;
      });
    
      const tableData = Object.values(categoryMap).map(item => {
        item.total = warehouses.reduce((sum, warehouse) => sum + (item[warehouse] || 0), 0);
        return item;
      });
    
      tableData.sort((a, b) => a.category.localeCompare(b.category));
    
      const warehouseTotals = {};
      let grandTotal = 0;
      
      warehouses.forEach(warehouse => {
        warehouseTotals[warehouse] = tableData.reduce((sum, row) => sum + (row[warehouse] || 0), 0);
        grandTotal += warehouseTotals[warehouse];
      });

      
      const warehouseSummary = warehouses.map(warehouse => ({
        [warehouseKey]: warehouse,
        quantity: warehouseTotals[warehouse] || 0
      })).filter(item => item.quantity > 0).sort((a, b) => b.quantity - a.quantity);
      
      return {
        tableData,
        warehouses,
        warehouseTotals,
        grandTotal,
        warehouseLocationSummary: dataSource === "byWarehouseLocation" ? warehouseSummary : [],
        storeTypeSummary: dataSource === "byStoreType" ? warehouseSummary : [],
        total: grandTotal
      };
    }
  };
  
  const generatePieChartData = (data) => {
    if (!data || !data.tableData || !data.warehouses || data.grandTotal === 0) {
      return null;
    }
    
    
    if (groupBy === "sku") {
      return null;
    }
    
    const generateColors = (count) => {
      const colors = [
        '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
        '#FF9F40', '#FF6384', '#C9CBCF', '#4BC0C0', '#FF6384'
      ];
      return Array.from({length: count}, (_, i) => colors[i % colors.length]);
    };

    
    const warehouseSummary = [];
    if (data.warehouses && data.warehouseTotals) {
      data.warehouses.forEach(warehouse => {
        const total = data.warehouseTotals[warehouse] || 0;
        if (total > 0) {
          warehouseSummary.push({
            label: warehouse,
            value: total
          });
        }
      });
    }

    if (warehouseSummary.length === 0) {
      return null;
    }

    
    warehouseSummary.sort((a, b) => b.value - a.value);

    const colors = generateColors(warehouseSummary.length);

    const pieChartData = {
      labels: warehouseSummary.map(item => item.label),
      datasets: [{
        data: warehouseSummary.map(item => item.value),
        backgroundColor: colors,
        borderColor: colors.map(color => color + '80'),
        borderWidth: 1
      }]
    };
    
    return pieChartData;
  };
  
  const processRegionData = () => {
    if (!reportData || !Array.isArray(reportData.byRegion)) {
      return { tableData: [], total: 0 };
    }
    const regions = [...new Set(reportData.byRegion.map(it => it?._id?.region).filter(Boolean))];
    const table = [];
    let total = 0;
    regions.forEach(region => {
      const items = reportData.byRegion.filter(it => it?._id?.region === region);
      const quantity = items.reduce((sum, it) => sum + (it?.totalSales || 0), 0);
      total += quantity;
      let details = [];
      if (groupBy === 'sku') {
        details = items.map(it => ({
          sku: it._id?.sku,
          productName: skuProductMap?.[it._id?.sku]?.product || 'Unknown Product',
          category: skuProductMap?.[it._id?.sku]?.category || 'Uncategorized',
          salesUnits: it.totalSales || 0,
          sohUnits: it.sohUnit || 0,
        }));
      } else if (groupBy === 'product') {
        const productMap = {};
        items.forEach(it => {
          const sku = it._id?.sku;
          const product = skuProductMap?.[sku]?.product || 'Unknown Product';
          const category = skuProductMap?.[sku]?.category || 'Uncategorized';
          if (!productMap[product]) {
            productMap[product] = { productName: product, category, salesUnits: 0, sohUnits: 0, skus: [] };
          }
          productMap[product].salesUnits += it.totalSales || 0;
          productMap[product].sohUnits += it.sohUnit || 0;
          productMap[product].skus.push(sku);
        });
        details = Object.values(productMap);
      } else if (groupBy === 'category') {
        const catMap = {};
        items.forEach(it => {
          const sku = it._id?.sku;
          const category = skuProductMap?.[sku]?.category || 'Uncategorized';
          if (!catMap[category]) {
            catMap[category] = { category, salesUnits: 0, sohUnits: 0, skus: [], products: new Set() };
          }
          catMap[category].salesUnits += it.totalSales || 0;
          catMap[category].sohUnits += it.sohUnit || 0;
          catMap[category].skus.push(sku);
          catMap[category].products.add(skuProductMap?.[sku]?.product || 'Unknown Product');
        });
        details = Object.values(catMap).map(c => ({ ...c, products: Array.from(c.products) }));
      }
      table.push({ region, quantity, details });
    });
    return { tableData: table.sort((a,b)=>b.quantity-a.quantity), total };
  };


  const generateRegionBarData = (data) => {
    if (!data || !data.tableData || data.tableData.length === 0) {
      return null;
    }
    
    const generateColors = (count) => {
      const colors = [
        '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
        '#FF9F40', '#FF6384', '#C9CBCF', '#4BC0C0', '#FF6384'
      ];
      return Array.from({length: count}, (_, i) => colors[i % colors.length]);
    };

    const topRegions = data.tableData.slice(0, 10);
    const colors = generateColors(topRegions.length);
    
    return {
      labels: topRegions.map(item => item.region),
      datasets: [{
          label: 'Quantity',
        data: topRegions.map(item => item.quantity),
        backgroundColor: colors,
        borderColor: colors.map(color => color + '80'),
          borderWidth: 1
      }]
    };
  };
  
  const fetchMonthToDateData = async () => {
    if (!filters.endDate) return;
    
    
    if (loadingMonthToDate) return;
    
    setLoadingMonthToDate(true);
    setMonthToDateData([]);
    setLastYearMonthToDateData([]); 
    setMonthToDateColorData([]); 
    
    try {
      
      const startDateStr = filters.startDate.toISOString().split('T')[0];
      const endDateStr = filters.endDate.toISOString().split('T')[0];
      
      
      const params = new URLSearchParams();
      params.append('startDate', startDateStr);
      params.append('endDate', endDateStr);
      
      
      if (filters.regions  && filters.regions.length > 0) {
        filters.regions.forEach(region => params.append('regions[]', region));
      }
      
      // Add category filter if exists
      if (filters.categoryNames && filters.categoryNames.length > 0) {
        filters.categoryNames.forEach(category => params.append('categoryNames[]', category));
      }
      
      // Add product filter if exists
      if (filters.productIds && filters.productIds.length > 0) {
        filters.productIds.forEach(productId => params.append('productIds[]', productId));
      }
      
      const tyEndpoint = `/reports/month-to-date?${params.toString()}`;
      const tyResponse = await superagent
        .get(`${backendUrl}${tyEndpoint}`)
        .set('Authorization', `Bearer ${localStorage.getItem("authJWT")}`)
        .set('Accept', 'application/json');
      
      if (tyResponse.body) {
        
        setMonthToDateData(tyResponse.body.byCategory || tyResponse.body || []);
        
        
        if (tyResponse.body.byColor) {
          setMonthToDateColorData(tyResponse.body.byColor);
        }
      }
      
      
      const lyStartDate = new Date(filters.startDate);
      lyStartDate.setFullYear(lyStartDate.getFullYear() - 1);
      const lyStartDateStr = lyStartDate.toISOString().split('T')[0];
      
      const lyEndDate = new Date(filters.endDate);
      lyEndDate.setFullYear(lyEndDate.getFullYear() - 1);
      const lyEndDateStr = lyEndDate.toISOString().split('T')[0];
      
      // Build query parameters for last year
      const lyParams = new URLSearchParams();
      lyParams.append('startDate', lyStartDateStr);
      lyParams.append('endDate', lyEndDateStr);
      
      // Add regions
      if (filters.regions && filters.regions.length > 0) {
        filters.regions.forEach(region => lyParams.append('regions[]', region));
      }
      
      // Add category filter if exists
      if (filters.categoryNames && filters.categoryNames.length > 0) {
        filters.categoryNames.forEach(category => lyParams.append('categoryNames[]', category));
      }
      
      // Add product filter if exists
      if (filters.productIds && filters.productIds.length > 0) {
        filters.productIds.forEach(productId => lyParams.append('productIds[]', productId));
      }
      
      const lyEndpoint = `/reports/month-to-date?${lyParams.toString()}`;
      const lyResponse = await superagent
        .get(`${backendUrl}${lyEndpoint}`)
        .set('Authorization', `Bearer ${localStorage.getItem("authJWT")}`)
        .set('Accept', 'application/json');
      
      if (lyResponse.body) {
        setLastYearMonthToDateData(lyResponse.body.byCategory || lyResponse.body || []);
      }
      
      
      if (tyResponse.body && lyResponse.body) {
        processMonthToDateComparison(
          tyResponse.body.byCategory || tyResponse.body || [], 
          lyResponse.body.byCategory || lyResponse.body || []
        );
      }
    } catch (error) {
      
    } finally {
      setLoadingMonthToDate(false);
    }
  };
  
  
  const processMonthToDateComparison = () => {
    if (!monthToDateData || !lastYearMonthToDateData) {
      return [];
    }
    
  
    const lyDataMap = new Map();
    
    lastYearMonthToDateData.forEach(category => {
      const productMap = new Map();
      category.products.forEach(product => {
        productMap.set(product.productName, product.quantity);
      });
      
      lyDataMap.set(category.subCategory, {
        productMap,
        total: category.total
      });
    });
    
    
    
    const combinedData = monthToDateData.map(tyCategory => {
      const lyCategory = lyDataMap.get(tyCategory.subCategory);
      const lyTotal = lyCategory ? lyCategory.total : 0;
      
    
      const diff = tyCategory.total - lyTotal;
      const percentChange = lyTotal > 0 ? (diff / lyTotal) * 100 : null;
      
    
      const productsWithComparison = tyCategory.products.map(tyProduct => {
        const lyQuantity = lyCategory && lyCategory.productMap.has(tyProduct.productName)
          ? lyCategory.productMap.get(tyProduct.productName)
          : 0;
        
        const productDiff = tyProduct.quantity - lyQuantity;
        const productPercentChange = lyQuantity > 0 ? (productDiff / lyQuantity) * 100 : null;
        
        return {
          ...tyProduct,
          lyQuantity,
          diff: productDiff,
          percentChange: productPercentChange
        };
      });
      
      return {
        subCategory: tyCategory.subCategory,
        products: productsWithComparison,
        total: tyCategory.total,
        lyTotal,
        diff,
        percentChange
      };
    });
    
    return combinedData;
  };
  
  const renderMonthToDateTable = () => {
    if (loadingMonthToDate) {
      return (
        <div className="flex justify-center items-center h-[300px]">
          <div className="flex flex-col items-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
            <span className="mt-2 text-sm text-gray-500">Loading data...</span>
          </div>
        </div>
      );
    }

    if (!monthToDateData || monthToDateData.length === 0) {
      return <div className="text-center py-4">No data available</div>;
    }
    
    const comparisonData = processMonthToDateComparison();
    
    
    const grandTotalTY = comparisonData.reduce((sum, category) => sum + (category.total || 0), 0);
    const grandTotalLY = comparisonData.reduce((sum, category) => sum + (category.lyTotal || 0), 0);
    const grandTotalDiff = grandTotalTY - grandTotalLY;
    const grandTotalPercentChange = grandTotalLY > 0 ? (grandTotalDiff / grandTotalLY) * 100 : null;
    
    return (
      <div className="overflow-x-auto relative">
        <div className="mb-6 bg-gray-50 p-4 rounded-lg border border-gray-200 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Comparison Period:</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white p-3 rounded-md border border-gray-100">
              <span className="font-medium text-primary block mb-1">This Year (TY):</span>
              <div className="text-sm">
                {format(new Date(filters.startDate), "MMMM d, yyyy")} - {format(new Date(filters.endDate), "MMMM d, yyyy")}
                <div className="text-gray-500 text-xs mt-1">
                  (Selected date range)
                </div>
              </div>
            </div>
            
            <div className="bg-white p-3 rounded-md border border-gray-100">
              <span className="font-medium text-primary block mb-1">Last Year (LY):</span>
              <div className="text-sm">
                {format(new Date(new Date(filters.startDate).setFullYear(new Date(filters.startDate).getFullYear() - 1)), "MMMM d, yyyy")} - {format(new Date(new Date(filters.endDate).setFullYear(new Date(filters.endDate).getFullYear() - 1)), "MMMM d, yyyy")}
                <div className="text-gray-500 text-xs mt-1">
                  (Same period last year)
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="border rounded-lg overflow-hidden shadow-sm">
          <Table className="w-full">
            <TableHeader>
              <TableRow className="bg-gray-50 border-b">
                <TableHead rowSpan={2} className="w-[180px]">Sub-Category</TableHead>
                <TableHead rowSpan={2} className="w-[220px]">Product Name</TableHead>
                <TableHead colSpan={2} className="text-center border-b">Quantity</TableHead>
                <TableHead colSpan={2} className="text-center border-b">Change</TableHead>
              </TableRow>
              <TableRow className="bg-gray-50">
                <TableHead className="text-center w-[100px]">TY</TableHead>
                <TableHead className="text-center w-[100px]">LY</TableHead>
                <TableHead className="text-center w-[100px]">Diff</TableHead>
                <TableHead className="text-center w-[100px]">%</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {comparisonData.map((category, categoryIndex) => (
                <>
                  {category.products.map((product, productIndex) => (
                    <TableRow key={`${categoryIndex}-${productIndex}`} className="hover:bg-gray-50">
                      {productIndex === 0 ? (
                        <TableCell 
                          className="font-medium bg-gray-50" 
                          rowSpan={category.products.length}
                        >
                          {category.subCategory}
                        </TableCell>
                      ) : null}
                      <TableCell className="max-w-[200px]">{product.productName}</TableCell>
                      <TableCell className="text-center font-medium">{product.quantity}</TableCell>
                      <TableCell className="text-center text-gray-600">{product.lyQuantity}</TableCell>
                      <TableCell className={`text-center ${product.diff > 0 ? 'text-green-600' : product.diff < 0 ? 'text-red-600' : ''}`}>
                        {product.diff > 0 ? '+' : ''}{product.diff}
                      </TableCell>
                      <TableCell className={`text-center ${product.percentChange > 0 ? 'text-green-600' : product.percentChange < 0 ? 'text-red-600' : ''}`}>
                        {product.percentChange !== null 
                          ? `${product.percentChange > 0 ? '+' : ''}${product.percentChange.toFixed(1)}%` 
                          : 'N/A'}
                      </TableCell>
                    </TableRow>
                  ))}
                  
                  <TableRow className="bg-gray-100">
                    <TableCell colSpan={2} className="font-bold">
                      {category.subCategory} Result
                    </TableCell>
                    <TableCell className="text-center font-bold">{category.total}</TableCell>
                    <TableCell className="text-center text-gray-700 font-medium">{category.lyTotal}</TableCell>
                    <TableCell className={`text-center font-bold ${category.diff > 0 ? 'text-green-600' : category.diff < 0 ? 'text-red-600' : ''}`}>
                      {category.diff > 0 ? '+' : ''}{category.diff}
                    </TableCell>
                    <TableCell className={`text-center font-bold ${category.percentChange > 0 ? 'text-green-600' : category.percentChange < 0 ? 'text-red-600' : ''}`}>
                      {category.percentChange !== null 
                        ? `${category.percentChange > 0 ? '+' : ''}${category.percentChange.toFixed(1)}%` 
                        : 'N/A'}
                    </TableCell>
                  </TableRow>
                </>
              ))}
              
              <TableRow className="bg-primary/10 border-t-2 border-primary/20">
                <TableCell colSpan={2} className="font-bold text-lg">
                  Total Result
                </TableCell>
                <TableCell className="text-center font-bold text-lg">{grandTotalTY}</TableCell>
                <TableCell className="text-center font-medium text-lg">{grandTotalLY}</TableCell>
                <TableCell className={`text-center font-bold text-lg ${grandTotalDiff > 0 ? 'text-green-600' : grandTotalDiff < 0 ? 'text-red-600' : ''}`}>
                  {grandTotalDiff > 0 ? '+' : ''}{grandTotalDiff}
                </TableCell>
                <TableCell className={`text-center font-bold text-lg ${grandTotalPercentChange > 0 ? 'text-green-600' : grandTotalPercentChange < 0 ? 'text-red-600' : ''}`}>
                  {grandTotalPercentChange !== null 
                    ? `${grandTotalPercentChange > 0 ? '+' : ''}${grandTotalPercentChange.toFixed(1)}%` 
                    : 'N/A'}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </div>
    );
  };

  
  const renderMonthToDateColorTable = () => {
    if (!monthToDateColorData || monthToDateColorData.length === 0) {
      return null; 
    }
    
    
    const totalQuantity = monthToDateColorData.reduce((sum, item) => sum + (item.quantity || 0), 0);
    
    
    const getColorStyle = (colorName) => {
      const colorMap = {
    
        'Black': 'bg-gray-900',
        'Blue': 'bg-blue-600',
        'Brown': 'bg-amber-800',
        'Green': 'bg-green-600',
        'Grey': 'bg-gray-500',
        'Navy': 'bg-blue-900',
        'Pink': 'bg-pink-400',
        'Red': 'bg-red-600',
        'White': 'bg-white border border-gray-200',
        'Yellow': 'bg-yellow-400',
        
        
        'Alphabet': 'bg-blue-200',
        
        
        'Berlin': 'bg-stone-300',
        'Biscuit': 'bg-amber-200',
        'Blush Pink': 'bg-pink-300',
        'Bone': 'bg-stone-100',
        'Brick': 'bg-red-700',
        'Bright Red': 'bg-red-500',
        'Brown / Ecru': 'bg-amber-700',
        'Burgundy': 'bg-red-800',
        'Burnt Orange': 'bg-orange-600',
        'Butter Yellow': 'bg-yellow-200',
        
        
        'Cancun - Large': 'bg-blue-100',
        'Cancun - Small': 'bg-blue-50',
        'Chalk White': 'bg-slate-50 border border-gray-200',
        'Charcoal': 'bg-gray-700',
        'Charcoal/Blue': 'bg-gray-700',
        'Charcoal/Red': 'bg-gray-700',
        'Charcoal/Yellow': 'bg-gray-700',
        'Chocolate Brown': 'bg-amber-900',
        'City': 'bg-slate-400',
        'Clay': 'bg-orange-300',
        'Coastal Blue': 'bg-sky-500',
        'Copper': 'bg-amber-600',
        'Cornflower Blue': 'bg-blue-400',
        'Cream': 'bg-amber-50 border border-gray-200',
        'Cucumber Green': 'bg-green-300',
        
        
        'Dark Brown': 'bg-amber-950',
        'Dark Burgundy': 'bg-red-950',
        
        
        'Electric Green': 'bg-lime-500',
        
        
        'Forest': 'bg-green-800',
        'Forest Green': 'bg-green-900',
        'French Blue': 'bg-indigo-600',
        'French Navy': 'bg-indigo-900',
        
        
        'Grape': 'bg-purple-700',
        
        
        'Hunter Green': 'bg-emerald-800',
        
        
        'Khaki': 'bg-yellow-700',
        
        
        'Light Blue': 'bg-blue-300',
        'Light Brown': 'bg-amber-400',
        'Light Grey': 'bg-gray-300',
        'Light Taupe': 'bg-stone-300',
        'Lilac': 'bg-purple-300',
        
        
        'Magenta': 'bg-fuchsia-600',
        'Mandarin Orange': 'bg-orange-500',
        'Marigold Yellow': 'bg-amber-500',
        'Matte Black': 'bg-black',
        'Matte Brown': 'bg-amber-800',
        'Matte Cream': 'bg-amber-50 border border-gray-200',
        'Matte Forest': 'bg-green-800',
        'Matte French Blue': 'bg-indigo-600',
        'Matte Light Grey': 'bg-gray-300',
        'Matte Moss': 'bg-olive-600',
        'Matte Plum': 'bg-purple-800',
        'Matte Red': 'bg-red-600',
        'Matte Sand': 'bg-yellow-100',
        'Midnight Black': 'bg-slate-950',
        'Mint': 'bg-green-200',
        'Mix': 'bg-gradient-to-r from-blue-500 to-purple-500',
        'Monochrome': 'bg-gradient-to-r from-gray-900 to-gray-100',
        'Moss': 'bg-olive-700',
        'Myrtle Green': 'bg-emerald-700',
        
        
        'Natural': 'bg-stone-200',
        'Natural Taupe': 'bg-neutral-400',
        'Navy with The Upside Pattern': 'bg-blue-900',
        'Nude': 'bg-orange-100',
        
        
        'Ocean Blue': 'bg-cyan-600',
        'Olive Green': 'bg-olive-600',
        'Oyster': 'bg-zinc-200',
        
        
        'Plum': 'bg-purple-800',
        'Plum / Blush Pink': 'bg-purple-700',
        
        
        'Red / Blush Pink': 'bg-red-400',
        'Rose Quartz': 'bg-pink-200',
        
        
        'Sage': 'bg-green-200',
        'Sage ': 'bg-green-200', 
        'Sand': 'bg-yellow-100',
        'Sapphire Blue': 'bg-blue-700',
        'Scarlet Red': 'bg-red-700',
        'Shadow': 'bg-gray-600',
        'Shadow Lavender': 'bg-purple-500',
        'Sky': 'bg-sky-400',
        'Slate Grey': 'bg-slate-600',
        'Snow': 'bg-gray-50 border border-gray-200',
        'Steel Blue': 'bg-blue-500',
        
        'Tan': 'bg-amber-300',
        'Taupe': 'bg-neutral-500',
        'Toff': 'bg-amber-700',
        'Transparent': 'bg-transparent border border-dashed border-gray-300',
        
        
        'Wheat': 'bg-amber-100',
        'Whiskey': 'bg-amber-600'
      };
      
      
      return colorMap[colorName] || 'bg-gray-400';
    };
    
    return (
      <div className="mt-10 pt-6 border-t border-gray-200">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">Color Analysis</h3>
          <div className="text-sm text-gray-500">Total units: {totalQuantity}</div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Table */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-100">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-semibold">Color</TableHead>
                  <TableHead className="text-right font-semibold">Quantity</TableHead>
                  <TableHead className="text-right font-semibold">%</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {monthToDateColorData.map((item, index) => {
                  const percentage = totalQuantity > 0 
                    ? ((item.quantity / totalQuantity) * 100).toFixed(1)
                    : 0;
                  
                  return (
                    <TableRow key={`color-${index}`} className="hover:bg-gray-50">
                      <TableCell className="flex items-center gap-2">
                        <div className={`w-4 h-4 rounded-full ${getColorStyle(item.color)}`}></div>
                        {item.color}
                      </TableCell>
                      <TableCell className="text-right font-medium">{item.quantity}</TableCell>
                      <TableCell className="text-right">{percentage}%</TableCell>
                    </TableRow>
                  );
                })}
                <TableRow className="font-bold bg-gray-50">
                  <TableCell>Total</TableCell>
                  <TableCell className="text-right">{totalQuantity}</TableCell>
                  <TableCell className="text-right">100%</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
          
          {/* Visual bar chart representation */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
            <h4 className="text-sm font-medium text-gray-500 mb-3">Color Distribution</h4>
            <div className="space-y-4">
              {monthToDateColorData.slice(0, 8).map((item, index) => {
                const percentage = totalQuantity > 0 
                  ? ((item.quantity / totalQuantity) * 100)
                  : 0;
                
                return (
                  <div key={`bar-${index}`} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="font-medium flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${getColorStyle(item.color)}`}></div>
                        {item.color}
                      </span>
                      <span>{item.quantity} ({percentage.toFixed(1)}%)</span>
                    </div>
                    <div className="h-2.5 w-full bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${getColorStyle(item.color)} rounded-full`}
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
              
              {monthToDateColorData.length > 8 && (
                <div className="text-center text-sm text-gray-500 mt-2">
                  And {monthToDateColorData.length - 8} more colors
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleFiltersChange = (newFilters) => {
    setFilters({ ...newFilters });
  };

  
  const warehouseLocationData = processTableData("byWarehouseLocation");
  const warehouseLocationPieData = generatePieChartData(warehouseLocationData);
  const warehouseLocationHierarchical = processHierarchicalData("byWarehouseLocation");

  const storeTypeData = processTableData("byStoreType");
  const storeTypePieData = generatePieChartData(storeTypeData);
  const storeTypeHierarchical = processHierarchicalData("byStoreType");
  
  const regionData = processRegionData();
  const regionBarData = generateRegionBarData(regionData);

  

  
  const activeFiltersCount = (filters.regions?.length || 0) + (filters.categoryNames?.length || 0) + (filters.productIds?.length || 0);

  return (
    <div className="page-content"> 
      <div className="container mx-auto p-4">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Sale State Report</h1>
          
                  <Button 
            onClick={() => setIsFilterModalOpen(true)}
                    variant="outline" 
            className="flex items-center gap-2"
          >
            <Filter className="h-4 w-4" />
            Filters
            {activeFiltersCount > 0 && (
              <span className="ml-1 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                {activeFiltersCount}
              </span>
            )}
                  </Button>
              </div>
              
        {/* Active Filters Display */}
        {activeFiltersCount > 0 && (
          <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium text-blue-900">Active Filters:</h4>
                    <Button
                onClick={() => setFilters(prev => ({ ...prev, regions: [], categoryNames: [], productIds: [] }))}
                variant="ghost" 
                size="sm"
                className="text-xs text-blue-700 hover:text-blue-900"
              >
                Clear All
                    </Button>
              </div>
            <div className="flex flex-wrap gap-2">
              {filters.regions?.map(region => (
                <span
                  key={`active-region-${region}`}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-800"
                >
                  📍 {region}
                </span>
              ))}
              {filters.categoryNames?.map(categoryName => (
                <span
                  key={categoryName}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800"
                >
                  📂 {categoryName}
                </span>
              ))}
              {filters.productIds?.map(productId => {
                const product = productData?.data?.find(p => p._id === productId);
                return product ? (
                  <span
                    key={productId}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800"
                  >
                    📦 {product.name}
                  </span>
                ) : null;
              })}
              </div>
          </div>
        )}
              
        <div className="mb-4">
          <div className="flex gap-4 items-start w-[20%]">
              <div className="flex-1 min-w-[120px]">
              <label className="block text-sm font-medium mb-1">Group By</label>
              <select
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="sku">SKU</option>
                <option value="product">Product</option>
                <option value="category">Category</option>
              </select>
            </div>
              </div>
            </div>
        
        {/* tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="justify-start mb-4">
          
            <TabsList className="mb-4 flex-nowrap justify-start min-w-[600px]">
              <TabsTrigger value="byWarehouseLocation" className="whitespace-nowrap">Warehouse Location</TabsTrigger>
              <TabsTrigger value="byStoreType" className="whitespace-nowrap">Store Type</TabsTrigger>
              <TabsTrigger value="byRegion" className="whitespace-nowrap">Region</TabsTrigger>
              <TabsTrigger value="monthToDate" className="whitespace-nowrap">Month to Date</TabsTrigger>
            </TabsList>
          
          
          <TabsContent value="byWarehouseLocation">
            <ByWarehouseLocationTab
              data={warehouseLocationData}
              hierarchicalData={warehouseLocationHierarchical}
              pieData={warehouseLocationPieData}
              loading={loading}
              filters={filters}
              groupBy={groupBy}
            />
          </TabsContent>
          
          <TabsContent value="byStoreType">
            <ByStoreTypeTab
              data={storeTypeData}
              hierarchicalData={storeTypeHierarchical}
              pieData={storeTypePieData}
              loading={loading}
              filters={filters}
              groupBy={groupBy}
            />
          </TabsContent>
          
          <TabsContent value="byRegion">
            <RegionTab
              data={regionData}
              barData={regionBarData}
              loading={loading}
              filters={filters}
              mainLabel="Region"
            />
          </TabsContent>
          
          <TabsContent value="monthToDate">
            <div className="space-y-6">
              {renderMonthToDateTable()}
              {renderMonthToDateColorTable()}
            </div>
          </TabsContent>
        </Tabs>

        {/* Filter Modal */}
        <SaleStateReportFilterModal
          isOpen={isFilterModalOpen}
          onClose={() => setIsFilterModalOpen(false)}
          filters={filters}
          onApplyFilters={handleFiltersChange}
          regionOptions={regionOptions}
        />
      </div>
    </div>
  );
}

export default SaleStateReport; 
